import { useIsTouchDevice } from "@h/useResponsive";
import { useNotifications } from "@h/useNotifications";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import ResponsiveSheet from "@ui/responsive-sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { Bell } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NotificationPanel } from "./NotificationPanel";

export function NotificationBell() {
  const { unreadCount } = useNotifications();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isTouch = useIsTouchDevice();

  const bell = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("notifications")}
      className="relative inline-flex items-center justify-center size-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      <Bell size={18} strokeWidth={1.75} />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-primary-strong text-primary-foreground label-micro font-mono leading-none flex items-center justify-center">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );

  // Icon-only at every width, so the name only ever surfaces on hover.
  const withTip = (node: ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="bottom">{t("notifications")}</TooltipContent>
    </Tooltip>
  );

  if (isTouch) {
    return (
      <>
        {withTip(bell(() => setOpen(true)))}
        <ResponsiveSheet open={open} onOpenChange={setOpen} hideTitle>
          <NotificationPanel onClose={() => setOpen(false)} inSheet />
        </ResponsiveSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {withTip(<PopoverTrigger asChild>{bell()}</PopoverTrigger>)}
      <PopoverContent align="end" className="p-0 w-auto">
        <NotificationPanel onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
